import React, { Suspense } from 'react';
import { generatePath, Redirect, Route, Switch } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from '@/hooks';
import { PrivateRoute } from '@/components/layout/routes';
import routes from '@/config/routes';
import { selectCurrentClientId } from '@/store/selectors/authSelectors';
import { ResourceContentSkeleton } from './ResourceContent.skeleton';

const ClientRoute = React.lazy(() => import('./content-routes/ResourceClientRoute'));
const CategoryRoute = React.lazy(() => import('./content-routes/ResourceCategoryRoute'));
const DatabaseRoute = React.lazy(() => import('./content-routes/ResourceDatabaseRoute'));
const ViewRoute = React.lazy(() => import('./content-routes/ResourceViewRoute'));
const FolderRoute = React.lazy(() => import('./content-routes/ResourceFolderRoute'));

const MissingDatabaseRouteHandler = React.lazy(
  () => import('@/components/common/resources/content-routes/ResourceMissingDatabaseRouteHandler'),
);
const MissingViewRouteHandler = React.lazy(
  () => import('@/components/common/resources/content-routes/ResourceMissingViewRouteHandler'),
);

const resourcesContentSelector = createSelector(selectCurrentClientId, currentClientId => ({
  currentClientId,
}));

const ResourcesContent = (): React.ReactElement => {
  const { currentClientId } = useAppSelector(resourcesContentSelector);

  if (!currentClientId) return <></>;

  return (
    <Suspense fallback={<ResourceContentSkeleton />}>
      <Switch>
        <PrivateRoute path={routes.sp.scheduling.resources.client} component={ClientRoute} exact />
        <PrivateRoute path={routes.sp.scheduling.resources.category} component={CategoryRoute} exact />
        <PrivateRoute path={routes.sp.scheduling.resources.folder} component={FolderRoute} exact />
        <Route path={routes.sp.scheduling.resources.database}>
          <MissingDatabaseRouteHandler>
            <PrivateRoute path={routes.sp.scheduling.resources.database} component={DatabaseRoute} exact />
            <Route path={routes.sp.scheduling.resources.view}>
              <MissingViewRouteHandler>
                <PrivateRoute path={routes.sp.scheduling.resources.view} component={ViewRoute} exact />
              </MissingViewRouteHandler>
            </Route>
          </MissingDatabaseRouteHandler>
        </Route>
        <Redirect to={generatePath(routes.sp.scheduling.resources.client, { client: currentClientId })} />
      </Switch>
    </Suspense>
  );
};

export { ResourcesContent };
